import type { ComponentPropsWithRef, ElementType, ReactElement, ReactNode, Ref } from 'react';
import React from 'react';

import classNames from 'classnames';

import styles from './Button.module.css';

// Legacy Button API for backward compatibility
export interface LegacyButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  variant?:
    | 'default'
    | 'accent'
    | 'destructive'
    | 'danger'
    | 'danger-solid'
    | 'ghost'
    | 'link'
    | 'primary'
    | 'outline'
    | 'secondary';
  size?: 'default' | 'icon' | 'xs' | 'sm' | 'md' | 'lg';
  asChild?: boolean;
  children?: ReactNode;
  forIconOnly?: boolean;
}

// The two variants that predate the design system render as their nearest
// design-system variant. They used to fall through to the old `Button`, which
// dropped every class (see `Button`), so a button with no variant shipped as
// the browser's bare grey button.
const legacyVariants = { default: 'primary', link: 'ghost' } as const;

type LegacyButtonComponent = <T extends ElementType = 'button'>(
  props: LegacyButtonProps & { ref?: Ref<T> },
) => ReactElement | null;

function LegacyButton<T extends ElementType = 'button'>(
  {
    variant = 'default',
    size = 'default',
    className,
    // Accepted for the old API and ignored: the children render inside the button.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    asChild,
    children,
    forIconOnly = false,
    ...props
  }: LegacyButtonProps,
  ref: ComponentPropsWithRef<T>['ref'],
): ReactElement | null {
  const resolved = variant === 'default' || variant === 'link' ? legacyVariants[variant] : variant;
  const dsSize = size === 'icon' ? 'sm' : size === 'default' ? 'md' : size;
  return (
    <button
      ref={ref}
      type="button"
      data-variant={resolved}
      className={classNames(
        styles.Button,
        styles.rv,
        {
          [styles.primary]: resolved === 'primary',
          [styles.accent]: resolved === 'accent',
          [styles.secondary]: resolved === 'secondary',
          [styles.outline]: resolved === 'outline',
          [styles.ghost]: resolved === 'ghost',
          [styles.destructive]: resolved === 'destructive' || resolved === 'danger',
          [styles.dangerSolid]: resolved === 'danger-solid',
          [styles.xs]: dsSize === 'xs',
          [styles.sm]: dsSize === 'sm',
          [styles.md]: dsSize === 'md',
          [styles.lg]: dsSize === 'lg',
          [styles.forIconOnly]: forIconOnly,
        },
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export default React.forwardRef<HTMLButtonElement>(LegacyButton) as LegacyButtonComponent;
